/* The sign-in pages' furniture.

   The *shape* of these pages is `portable/login/Access.jsx`: a centred heading, a panel,
   mono uppercase field labels, a full-width button that greys out until the form is
   answerable, a quiet link to the other method, a rule, then the federated buttons.

   The *surface* is `portable/app/CodeNerveApp.jsx` -- its `CARD`, `LABEL`, `INPUT` and
   `AppInput` form primitives, the ones it files under "form primitives (dark)". The
   prototype's own login page is the marketing site's light palette because it is served
   from `codenerve.ai`; this one is served from `app.codenerve.ai`, which is dark
   throughout, and the app has dark primitives for exactly this reason. */

export const CARD = {
  width: "min(430px,92vw)",
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "20px",
  boxShadow: "0 50px 110px -60px #000",
  padding: "26px 30px 30px",
};

export const LABEL = {
  display: "block",
  font: "var(--text-caption)",
  fontFamily: "var(--font-mono)",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--text-faint)",
  marginBottom: "clamp(5px,0.9vh,9px)",
};

export const INPUT = {
  width: "100%",
  boxSizing: "border-box",
  font: "var(--text-body-16)",
  fontFamily: "var(--font-body)",
  color: "var(--text-primary)",
  background: "var(--surface-subtle)",
  border: "1px solid var(--border)",
  borderRadius: "10px",
  padding: "clamp(9px,1.5vh,12px) 14px",
  outline: "none",
};

export function Req() {
  return (
    <span aria-hidden="true" style={{ color: "var(--accent)", marginLeft: "4px", verticalAlign: "-0.15em" }}>
      *
    </span>
  );
}

export function AppInput({ label, req, prefix, ...rest }) {
  return (
    <label style={{ display: "block", marginBottom: "clamp(9px,1.5vh,16px)" }}>
      <span style={LABEL}>
        {label}
        {req && <Req />}
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        {prefix && (
          <span
            style={{
              font: "var(--text-body-14)",
              fontFamily: "var(--font-mono)",
              color: "var(--text-faint)",
              whiteSpace: "nowrap",
            }}
          >
            {prefix}
          </span>
        )}
        <input {...rest} style={INPUT} />
      </span>
    </label>
  );
}

/* Submission failure is the one state the design does not already draw, because nothing
   could fail before. It renders null until there is something to say, so every state that
   existed before is untouched. */
export function SubmitError({ message }) {
  if (!message) return null;
  return (
    <span role="alert" style={{ font: "var(--text-caption)", color: "var(--danger)" }}>
      {message}
    </span>
  );
}

/* Charcoal-on-white when the form can be submitted, flat when it cannot, so the disabled
   state is visible rather than inferred from a click that does nothing. */
export function SubmitButton({ ready, onClick, children, type = "button" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={!ready}
      style={{
        height: "48px",
        borderRadius: "10px",
        border: "none",
        cursor: ready ? "pointer" : "not-allowed",
        font: "var(--text-label)",
        color: ready ? "var(--btn-primary-ink)" : "var(--text-disabled)",
        background: ready ? "var(--btn-primary-bg)" : "var(--surface-elevated)",
        transition: "background 320ms var(--ease-out), color 320ms var(--ease-out)",
      }}
    >
      {children}
    </button>
  );
}

export function QuietLink({ children, onClick }) {
  return (
    <a
      href="#"
      onClick={(event) => {
        event.preventDefault();
        onClick();
      }}
      style={{ color: "var(--text-primary)" }}
    >
      {children}
    </a>
  );
}

/* The page around the card: centred, with the mark above the heading. */
export function AccessPage({ title, lead, children, footer }) {
  return (
    <main
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: "clamp(24px,6vh,72px) 20px",
        background: "var(--bg)",
      }}
    >
      <div style={{ width: "min(430px,92vw)" }}>
        <h1
          style={{
            font: "500 clamp(24px,2.6vw,34px)/1.06 var(--font-display)",
            letterSpacing: "-0.03em",
            color: "var(--text-primary)",
            textAlign: "center",
            marginBottom: "10px",
          }}
        >
          {title}
        </h1>
        <p
          style={{
            font: "var(--text-body-14)",
            color: "var(--text-muted)",
            textAlign: "center",
            marginBottom: "26px",
          }}
        >
          {lead}
        </p>
        <div style={CARD}>{children}</div>
        {footer && (
          <p
            style={{
              font: "var(--text-caption)",
              color: "var(--text-muted)",
              textAlign: "center",
              marginTop: "22px",
            }}
          >
            {footer}
          </p>
        )}
      </div>
    </main>
  );
}
